import React, { useMemo, useRef, useState, useCallback } from "react";
import "./donut-chart.scss";
import { Doughnut } from "react-chartjs-2";
import { TrimString } from "../../../Utils/StringTrimmer";

/**
 * Generic Donut Chart Component
 * 
 * @param {Object} props - Component props
 * @param {Array} props.graphData - Array of data objects: [{ label: string, value: number, color: string, colorId?: string }]
 * @param {number} props.height - Height of the chart (default: 150)
 * @param {number} props.thickness - Thickness of the donut ring (0-100, default: 30)
 * @param {React.Ref} props.forwardedRef - Ref to expose expandSpecificSegment method
 * @param {string} props.className - Additional CSS classes for the container
 * @param {string} props.emptyStateText - Text to display when total is 0 (default: "0%")
 * @param {string} props.emptyStateColor - Color for empty state ring (default: "rgb(235, 237, 241)")
 * @param {number} props.labelTruncateLength - Maximum length for label text (default: 10)
 * @param {number} props.expandedLabelTruncateLength - Maximum length for expanded label text (default: 15)
 * @param {number} props.rotation - Rotation angle in radians (default: 0.7 * Math.PI)
 * @param {number} props.hoverBorderWidth - Border width on hover (default: 10)
 * @param {number} props.expandedBorderWidth - Border width when segment is expanded (default: 10)
 * @param {number} props.hoverOffset - Offset on hover (default: 8)
 * @param {number} props.decimalPlaces - Number of decimal places for percentage (default: 2)
 * @param {Function} props.onHoverChange - Callback when hover state changes (isHovered: boolean) => void
 * @returns {JSX.Element} DonutChart component
 */
const DonutChart = ({
  height = 150,
  width = 225,
  graphData = [],
  thickness = 30,
  forwardedRef,
  className = "",
  emptyStateText = "0%",
  emptyStateColor = "rgb(235, 237, 241)",
  labelTruncateLength = 10,
  expandedLabelTruncateLength = 15,
  rotation = 0.7 * Math.PI,
  hoverBorderWidth = 10,
  expandedBorderWidth = 10,
  hoverOffset = 8,
  decimalPlaces = 2,
  onHoverChange,
}) => {
  const [labelName, setLabelName] = useState({ name: "", percent: "" });
  const chartRef = useRef(null);

  const calculatedThickness = 100 - (thickness ?? 31);
  const cutoutPercentage = calculatedThickness;

  const data = useMemo(() => {
    let graph_data = [...graphData].filter((item) => item?.value >= 0);
    let labels = [...graph_data].map((item) => item.label);
    let data_list = [...graph_data].map((item) => item.value);
    let colors = [...graph_data].map((item) => item.color);
    return {
      labels: labels,
      datasets: [
        {
          label: "Dataset",
          data: data_list,
          backgroundColor: colors,
          borderColor: colors,
          hoverBorderWidth: hoverBorderWidth,
          borderCapStyle: "butt",
          hoverOffset: hoverOffset,
        },
      ],
    };
  }, [graphData, hoverBorderWidth, hoverOffset]);

  const emptyStateData = {
    labels: ["Empty"],
    datasets: [
      {
        label: "Empty Dataset",
        data: [30],
        backgroundColor: [emptyStateColor],
        borderColor: [emptyStateColor],
        borderCapStyle: "butt",
      },
    ],
  };

  const Total = useMemo(() => {
    return data.datasets[0].data.reduce((accumulator, currentValue) => {
      return accumulator + currentValue;
    }, 0);
  }, [data]);

  const handleHover = useCallback((event, array) => {
    if (array && array.length > 0) {
      const index = array[0]._index;

      setLabelName({
        name: data.labels[index] ?? "",
        percent: `${((data.datasets[0].data[index] / Total) * 100).toFixed(
          decimalPlaces
        )}%`,
      });
      onHoverChange?.(true);
    } else {
      setLabelName({ name: "", percent: "" });
      onHoverChange?.(false);
    }
  }, [data, Total, decimalPlaces, onHoverChange]);

  const options = useMemo(() => ({
    rotation: rotation,
    hover: {
      animationDuration: 50,
    },
    legend: {
      display: false,
    },
    maintainAspectRatio: true,
    onHover: handleHover,
    plugins: {
      datalabels: {
        display: false,
      },
    },
    cutoutPercentage: cutoutPercentage,
    cutout: cutoutPercentage,
    tooltips: false,
  }), [rotation, cutoutPercentage, handleHover]);

  const preDataOptions = useMemo(() => ({
    rotation: rotation,
    hover: false,
    legend: {
      display: false,
    },
    maintainAspectRatio: true,
    plugins: {
      datalabels: {
        display: false,
      },
    },
    cutoutPercentage: cutoutPercentage,
    cutout: cutoutPercentage,
    tooltips: false,
  }), [rotation, cutoutPercentage]);

  function expandSpecificSegment(_colorId) {
    const chartInstance = chartRef.current?.chartInstance;

    if (!chartInstance) return;

    const datasetIndex = 0;
    const _index = graphData.findIndex((item) => item.colorId === _colorId);

    if (_index !== -1) {
      chartInstance.data.datasets[datasetIndex].borderWidth = data.labels.map(
        (_, index) => (index === _index ? expandedBorderWidth : 1)
      );

      setLabelName({
        name: TrimString(data.labels[_index], expandedLabelTruncateLength) ?? "",
        percent: `${((data.datasets[0].data[_index] / Total) * 100).toFixed(
          decimalPlaces
        )}%`,
      });
      onHoverChange?.(true);
    } else {
      chartInstance.data.datasets[datasetIndex].borderWidth = data.labels.map(
        (_, index) => 1
      );
      setLabelName({ name: "", percent: "" });
      onHoverChange?.(false);
    }

    chartInstance.update();
  }

  // Using forwardedRef to pass the ref to the parent
  React.useImperativeHandle(forwardedRef, () => ({
    expandSpecificSegment,
  }));

  const containerClassName = `donut-chart-container zoom-independent py-3 position-relative ${className}`.trim();

  return (
    <div className={containerClassName}>
      {Total === 0 ? (
        <div className="w-100 d-flex align-items-center justify-content-center position-relative">
          <Doughnut options={preDataOptions} data={emptyStateData} height={height} width={width}/>
          <div className="position-absolute f-500 f-18 f-grey d-flex align-items-center justify-content-center">
            {emptyStateText}
          </div>
        </div>
      ) : (
        <>
          <Doughnut options={options} data={data} height={height} ref={chartRef} width={width} />
          <div
            className="position-absolute"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
            }}
          >
            <div className="f-18 f-500 mb-2 d-flex align-items-center justify-content-center">
              {labelName.percent}
            </div>
            <div
              className="f-12 f-500 f-grey d-flex align-items-center justify-content-center"
              title={labelName.name}
            >
              {TrimString(labelName.name, labelTruncateLength)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DonutChart;